import type { Database, DatabaseReference } from 'firebase/database';
import {
  child,
  get,
  getDatabase,
  onDisconnect,
  ref,
  remove,
  serverTimestamp,
  set,
} from 'firebase/database';
import { omitBy, values } from 'lodash';
import { makeAutoObservable } from 'mobx';

import duplicateRTMSessionConfirmAlert from '../../../components/RTMSessionScreen/RTMSessionConfirmAlert';
import getEnvironment from '../../../config/environment';
import firebaseApp from '../../../config/firebase';
import { getSessionCompletionUpdates } from '../helper';
import Paths from '../paths';
import type { IDataSourceFirebaseStore } from './types';

export class DataSourceFirebaseStore implements IDataSourceFirebaseStore {
  private db: Database | undefined;

  constructor() {
    makeAutoObservable(this);
    this.initializeDatabase();
  }

  private initializeDatabase() {
    try {
      if (!firebaseApp) {
        throw new Error('Firebase app is not initialized');
      }
      this.db = getDatabase(firebaseApp);
    } catch (error) {
      console.error('Failed to initialize Firebase Database', error);
    }
  }

  setupDisconnectHandlers = async (
    sessionKey: string,
    deviceId: string,
    clinicId: string,
  ): Promise<{ success: boolean; error?: string }> => {
    if (!this.db) {
      return {
        success: false,
        error: 'Failed to initialize Firebase Database',
      };
    }
    try {
      const sessionRef = ref(this.db, Paths.activeSession(clinicId, sessionKey));
      const deviceRef = child(child(sessionRef, 'devices'), deviceId);
      const clinicRootRef = ref(this.db, Paths.clinicRoot(clinicId));
      const activeSessionsSnapshot = await get(sessionRef); // use getinstead of passing activeSessionsSnapshot to ensure the setupDisconnectHandlers for last values on db

      const devices = activeSessionsSnapshot.child('devices').val() || {};
      const deviceNumber = devices[deviceId];

      // Cancel ALL existing handlers
      await onDisconnect(deviceRef).cancel();
      await onDisconnect(clinicRootRef).cancel();
      // Add marker on disconnect
      await onDisconnect(child(child(sessionRef, 'disconnected'), deviceId)).set(serverTimestamp());
      await onDisconnect(deviceRef).remove();
      // Set up session leaders cleanup if we have a device number
      if (deviceNumber !== undefined) {
        await onDisconnect(ref(this.db, Paths.sessionLeader(clinicId, deviceNumber))).remove();
      }
      if (!getEnvironment().isProd) {
        console.log('4-setupDisconnectHandlers-deviceCount', Object.keys(devices).length);
      }
      // Handle session completion
      if (Object.keys(devices).length === 1 && devices[deviceId]) {
        const startTime = activeSessionsSnapshot.child('startTime').val();
        if (!startTime) {
          throw new Error('Session start time is missing.');
        }
        const metadata = activeSessionsSnapshot.child('metadata').val() || {};
        const [clinicianId, patientId] = sessionKey.split('|');
        const updates = getSessionCompletionUpdates(
          sessionKey,
          clinicianId,
          patientId,
          startTime,
          metadata,
        );
        await onDisconnect(clinicRootRef).update(updates);
        await onDisconnect(child(child(sessionRef, 'disconnected'), deviceId)).cancel();
      }
      return { success: true };
    } catch (e) {
      console.error(`Failed to setup disconnect handlers: ${e.message}`, e);
      return { success: false, error: e.message };
    }
  };

  handleSessionLeadership = async (
    sessionRef: DatabaseReference,
    deviceNumber: number,
    clinicId: string,
    isActiveDevice: boolean,
  ) => {
    if (!this.db) {
      console.error('Failed to initialize Firebase Database');
      return;
    }
    try {
      const sessionSnapshot = await get(sessionRef);
      const devices = sessionSnapshot.exists()
        ? Object.entries(sessionSnapshot.child('devices').val() || {}).reduce(
            (acc, [key, value]) => {
              acc[key] = (value || 0) as number;
              return acc;
            },
            {} as Record<string, number>,
          )
        : {};
      // Check if this device is the session leader (lowest index in session)
      const isSessionLeader = Object.values(devices).every((num) => Number(num) >= deviceNumber);
      // Reference to this device's potential leader entry
      const leaderRef = ref(this.db, Paths.sessionLeader(clinicId, deviceNumber));

      if (values(devices)?.includes(deviceNumber)) {
        if (isSessionLeader) {
          await onDisconnect(leaderRef).remove(); // Remove leader entry when the session ends
          await set(leaderRef, true); // Mark the device as a leader
        } else {
          if (isActiveDevice) {
            duplicateRTMSessionConfirmAlert(omitBy(devices, (num) => num === deviceNumber));
          }
          // Remove from leaders list if not the session leader
          await set(leaderRef, null); // Set to null to remove the leader entry
          await remove(leaderRef); // Ensure removal from the database
          await onDisconnect(leaderRef).cancel(); // Cancel the onDisconnect cleanup
        }
      }
    } catch (error) {
      console.error('Error handling session leadership:', error);
    }
  };
}
